import { TwainGameId } from 'betgames-contract-js';
import { GameStatus } from 'twain-sport-fe-mono';
import { BettingOptionsGroupsTag } from 'enums/BettingOptionsGroupsTag';
import {
    IBettingOptionsSection,
    IBettingOptionsGroupList,
    IBettingOptionsSectionData,
} from 'interfaces/IBettingOptionsGroupList';
import { IAppState } from './interfaces';

export const appSelectors = {
    getGames: (state: IAppState) =>
        state.games.filter((game) => game.status !== GameStatus.DISABLED),

    getAllGames: (state: IAppState) => state.games,

    getAvailableGames: (state: IAppState) =>
        state.games.filter((game) => game.status === GameStatus.AVAILABLE),

    isGameAvailable: (gameId: TwainGameId) => (state: IAppState) =>
        appSelectors
            .getGames(state)
            .some((game) => game.id === gameId && game.status === GameStatus.AVAILABLE),

    isGameComingSoon: (gameId: TwainGameId) => (state: IAppState) =>
        appSelectors
            .getGames(state)
            .some((game) => game.id === gameId && game.status === GameStatus.COMING),

    getGameById: (gameId: TwainGameId) => (state: IAppState) =>
        appSelectors.getAllGames(state).find((game) => game.id === gameId),

    getMatchBettingOptionsGroups:
        (gameId: TwainGameId) =>
        (state: IAppState): IBettingOptionsGroupList =>
            appSelectors.getGameById(gameId)(state)?.bettingOptionMap.tournamentMatch || {},

    getMatchBettingOptionsDefaultGroup: (gameId: TwainGameId) => (state: IAppState) =>
        Object.entries(appSelectors.getMatchBettingOptionsGroups(gameId)(state)).find(
            ([, object]) => object.tags.includes(BettingOptionsGroupsTag.DEFAULT),
        )?.[0],

    getAllMatchBettingOptions:
        (gameId: TwainGameId) =>
        (state: IAppState): IBettingOptionsSection =>
            Object.entries(appSelectors.getMatchBettingOptionsGroups(gameId)(state)).reduce(
                (acc, [, group]) => {
                    Object.assign(acc, group.bettingOptions);
                    return acc;
                },
                {},
            ),

    getTournamentBettingOptionsGroups:
        (gameId: TwainGameId) =>
        (state: IAppState): IBettingOptionsGroupList =>
            appSelectors.getGameById(gameId)(state)?.bettingOptionMap.tournament,

    getAllTournamentBettingOptions:
        (gameId: TwainGameId) =>
        (state: IAppState): IBettingOptionsSection =>
            Object.entries(appSelectors.getTournamentBettingOptionsGroups(gameId)(state)).reduce(
                (acc, [, group]) => {
                    Object.assign(acc, group.bettingOptions);
                    return acc;
                },
                {},
            ),

    getAllBettingOptions:
        (gameId: TwainGameId) =>
        (state: IAppState): IBettingOptionsSection => ({
            ...appSelectors.getAllMatchBettingOptions(gameId)(state),
            ...appSelectors.getAllTournamentBettingOptions(gameId)(state),
        }),

    getBettingOptionsGroupData:
        (gameId: TwainGameId, groupName: string) =>
        (state: IAppState): IBettingOptionsSectionData =>
            appSelectors.getAllBettingOptions(gameId)(state)[groupName],

    getLogo: (state: IAppState) => state.logo,

    getPartnerCode: (state: IAppState) => state.partnerCode,

    getShowBalance: (state: IAppState) => state.showBalance,

    getShowLogo: (state: IAppState) => state.showLogo,

    getLoadingScreen: (state: IAppState) => state.loadingScreen,

    isMultiBetAvailable: (state: IAppState) =>
        appSelectors.getAvailableGames(state).length >= 2 && state.isBetCombinationEnabled,
};
